/**
 * HeicDecode — shared HEIC/HEIF → JPEG/PNG decoder for the tool pages that
 * accept user photo uploads (heic-converter, photo-repair, photo-colorize,
 * 3d-photo-to-model, animated-avatar, ocr-text-extractor, sheet-music-to-midi).
 *
 * Replaces the per-tool heic2any@0.0.4 loaders. heic2any bundles an old libheif
 * that fails to decode HEIC produced by iOS 18 / iPhone 15+ ("format_unsupported"
 * — the largest HEIC failure class in our telemetry) and needs `unsafe-eval`.
 * heic-to (vendored /csp build, libheif 1.22.2) decodes current iPhone HEIC and
 * runs under our strict-dynamic CSP without `unsafe-eval`. Same loader contract
 * as redact-photo/decode.js + slideshow-utils.js (proven on prod + the RU edge).
 *
 * API:
 *   HeicDecode.isHeic(file)                         -> bool
 *   HeicDecode.toBlob(file, type?, quality?, emit?) -> Promise<Blob>
 *                                                      (jpeg @0.92 by default)
 *   HeicDecode.pending()                            -> jobs waiting on the worker
 *
 * ⚠️ For CONVERTING photos (batch, output files) use window.HeicConvert
 * (public/js/heif/heic-convert.js): a worker pool that decodes on the GPU via
 * WebCodecs where the browser has an HEVC decoder (0.3 s per 12 MP photo
 * against ~2.3 s here), falls back to this same libheif, converts Display P3
 * to sRGB and carries Exif. This module stays the right tool for a page that
 * merely needs to READ an iPhone photo (normalize() before its own decoder).
 *
 * Calls are dispatched SERIALLY (there is exactly one libheif worker behind
 * this module) and each job's timeout budget starts when it reaches that
 * worker — see the budget block below for why that distinction is the whole
 * point.
 *
 * `emit` is an optional telemetry callback (event, payload) — pass the tool's
 * __ae/__aeFeature wrapper so the CDN-fallback path stays observable.
 */
(function () {
    'use strict';

    // Same-origin primary (self-hosted, RU VPS-served for ru locale). The CDN
    // fallback routes through the same-origin /cdn/jsdelivr cache proxy — NEVER a
    // bare external host: (1) RKN can block jsdelivr in Russia; (2) our CSP uses
    // 'strict-dynamic', which disables the https: host-allowlist, so a
    // cross-origin import would be blocked regardless. /cdn/jsdelivr is
    // same-origin → always allowed, edge-cached.
    var VENDOR = '/vendor/heic-to/csp/heic-to.js';
    var CDN = '/cdn/jsdelivr/npm/heic-to@1.5.2/dist/csp/heic-to.js';
    var LOAD_TIMEOUT_MS = 15000;
    var _modPromise = null;

    // ── Decode budget: real WORK time, never queue time ─────────────────────
    // heic-to owns ONE module-level worker (see the vendored bundle: `let i`
    // cached, every call postMessage's into the same thread). A flat 30 s race
    // started at CALL time therefore measured how long a job waited behind
    // OTHER healthy jobs. Measured 2026-08-25 on the live page: 120 concurrent
    // decodes of one healthy 293 KB fixture → 76 succeed, 44 fail, the cut
    // landing exactly at 30 036 ms of WAITING (`tests/playwright/heic-decode-
    // queue-budget.mjs`). In prod that class is 317 tool_error rows / 59
    // sessions / 90 d on /heic-converter alone — every one of them a photo the
    // user had, marked «формат не поддерживается».
    //
    // Two changes make the budget mean something:
    //   1. dispatch is SERIAL here, so "we called heicTo" == "the worker
    //      started" — the only way to time work rather than waiting, given
    //      heic-to exposes no progress and no terminate;
    //   2. the budget scales with input size, because decode cost scales with
    //      pixels. Measured throughput on a fast desktop: 293 KB in 341 ms p50
    //      (≈0.86 MB/s of input); the allowance below is that times ~10 for
    //      slow hardware, plus a fixed start-up margin. A 3 MB iPhone photo
    //      gets 46 s where it needs ~3.5 s here.
    // FLOOR is the old flat budget: scaling must only ever GIVE a file more
    // time than it had, never less. A 0.5 MB photo would otherwise come out at
    // 16 s, and the six other tools on this module have not been re-measured
    // on slow phones.
    var CONVERT_BUDGET_FLOOR_MS = 30000;
    var CONVERT_BUDGET_BASE_MS = 10000;
    var CONVERT_BUDGET_PER_MB_MS = 12000;
    var CONVERT_BUDGET_MAX_MS = 180000;

    function convertBudgetMs(file) {
        var mb = (file && file.size ? file.size : 0) / 1048576;
        return Math.max(CONVERT_BUDGET_FLOOR_MS, Math.min(CONVERT_BUDGET_MAX_MS,
            CONVERT_BUDGET_BASE_MS + Math.ceil(mb * CONVERT_BUDGET_PER_MB_MS)));
    }

    function isHeic(file) {
        if (!file) return false;
        if (/\.(heic|heif)$/i.test(file.name || '')) return true;
        var type = (file.type || '').toLowerCase();
        return type === 'image/heic' || type === 'image/heif';
    }

    function loadModule(emit) {
        if (_modPromise) return _modPromise;
        _modPromise = new Promise(function (resolve, reject) {
            var settled = false;
            var timer = setTimeout(function () {
                if (settled) return;
                settled = true;
                _modPromise = null;
                var e = new Error('heic-to-loader-timeout');
                e.code = 'HEIC_LOADER_TIMEOUT';
                reject(e);
            }, LOAD_TIMEOUT_MS);
            import(VENDOR)
                .catch(function () {
                    if (settled) return Promise.reject(new Error('aborted-after-timeout'));
                    if (emit) emit('heic_load_cdn_fallback', {});
                    return import(CDN);
                })
                .then(function (mod) {
                    if (settled) return;
                    settled = true;
                    clearTimeout(timer);
                    if (!mod || typeof mod.heicTo !== 'function') {
                        _modPromise = null;
                        var e = new Error('heic-to-missing-after-load');
                        e.code = 'HEIC_LOAD_FAILED';
                        reject(e);
                        return;
                    }
                    resolve(mod);
                })
                .catch(function (err) {
                    if (settled) return;
                    settled = true;
                    clearTimeout(timer);
                    _modPromise = null;
                    reject(err);
                });
        });
        return _modPromise;
    }

    // ── Serial dispatch ─────────────────────────────────────────────────────
    // One worker downstream, so one job in flight here. Callers may fire as
    // many decodes as they like (a batch tool typically runs a preview chain
    // and a convert chain at once) — they queue, and each one's budget starts
    // when it reaches the worker.
    //
    // The slot is released when the UNDERLYING promise settles, not when our
    // budget expires: a job we gave up on is still occupying the worker, and
    // starting the next one on top of it is what turned a single slow file
    // into a session-long cascade (prod: 48 sessions with a 1-2 file batch
    // produced 291 timeouts — the same file re-tried into an ever busier
    // worker).
    var _queue = [];
    var _busy = false;

    function _pump() {
        if (_busy || !_queue.length) return;
        _busy = true;
        var job = _queue.shift();
        var settled = false;
        var timer = setTimeout(function () {
            if (settled) return;
            settled = true;
            var e = new Error('heic-convert-timeout');
            e.code = 'HEIC_CONVERT_TIMEOUT';
            job.reject(e);
        }, convertBudgetMs(job.file));

        var underlying;
        try {
            underlying = job.mod.heicTo({
                blob: job.file,
                type: job.type || 'image/jpeg',
                quality: (job.quality == null ? 0.92 : job.quality),
            });
        } catch (err) {
            underlying = Promise.reject(err);
        }
        Promise.resolve(underlying).then(function (out) {
            clearTimeout(timer);
            if (!settled) { settled = true; job.resolve(Array.isArray(out) ? out[0] : out); }
            _busy = false; _pump();
        }, function (err) {
            clearTimeout(timer);
            if (!settled) { settled = true; job.reject(err); }
            _busy = false; _pump();
        });
    }

    function toBlob(file, type, quality, emit) {
        return loadModule(emit).then(function (mod) {
            return new Promise(function (resolve, reject) {
                _queue.push({ mod: mod, file: file, type: type, quality: quality, resolve: resolve, reject: reject });
                _pump();
            });
        });
    }

    /** How many decodes are waiting for the worker (0 when idle). Lets a batch
     *  tool hold back a background preview chain instead of racing its own
     *  conversion for the single worker. */
    function pending() { return _queue.length + (_busy ? 1 : 0); }

    // Convenience: returns the file unchanged when it isn't HEIC/HEIF, otherwise
    // decodes it to a JPEG File. Lets any image tool accept iPhone photos with a
    // single `file = await HeicDecode.normalize(file)` before its decode path.
    function normalize(file, emit) {
        if (!isHeic(file)) return Promise.resolve(file);
        return toBlob(file, 'image/jpeg', 0.92, emit).then(function (blob) {
            return new File([blob], ((file && file.name) || 'image').replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
        });
    }

    window.HeicDecode = { isHeic: isHeic, toBlob: toBlob, normalize: normalize, loadModule: loadModule, pending: pending };
})();
